import React, { useState, useRef, useEffect, useCallback } from 'react';
// Ensure you have installed lucide-react: npm install lucide-react
import { Bot, User, Send, Circle, Copy, Check } from 'lucide-react';
// Ensure you have installed react-markdown and remark-gfm:
// npm install react-markdown remark-gfm
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';

// --- Theme Constants ---
const BG_COLOR = 'bg-black';
const USER_MSG_BG = 'bg-blue-600';
const AI_MSG_BG = 'bg-zinc-800';
const BUTTON_COLOR = 'bg-blue-600 hover:bg-blue-700 text-white';
const DISABLED_BUTTON = 'bg-zinc-700 text-zinc-400 cursor-not-allowed';
const DISCLAIMER_TEXT = "Echidna can make mistakes. Verify important information.";
const CODE_BLOCK_BG = 'bg-zinc-950';
const CODE_TEXT_COLOR = 'text-zinc-200';
const INLINE_CODE_BG = 'bg-zinc-700';
const INLINE_CODE_TEXT = 'text-emerald-400';
const LOGO_URL = 'https://cybverlake-test.s3.ap-south-1.amazonaws.com/ChatGPT_Image_Apr_4__2025__11_08_39_AM-removebg-preview.png';

// --- Helper Function to Parse AI Response (Handles Partial Streams) ---
function parseAIResponseStreaming(rawText) {
    // ... (parsing function remains the same) ...
    const reasonStartTag = '<reason>';
    const reasonEndTag = '</reason>';
    const separator = '\n\n'; // Separator between reasoning and response

    let reasoning = null;
    let response = null;

    const startIdx = rawText.indexOf(reasonStartTag);
    let responseStartIdx = -1; // Where the actual response text begins

    if (startIdx !== -1) {
        const endIdx = rawText.indexOf(reasonEndTag, startIdx + reasonStartTag.length);
        if (endIdx !== -1) {
            reasoning = rawText.substring(startIdx + reasonStartTag.length, endIdx).trim();
            const potentialSeparatorIdx = endIdx + reasonEndTag.length;
            if (rawText.substring(potentialSeparatorIdx, potentialSeparatorIdx + separator.length) === separator) {
                responseStartIdx = potentialSeparatorIdx + separator.length;
                response = rawText.substring(responseStartIdx).trimStart();
            } else {
                 response = null;
            }
        } else {
            reasoning = rawText.substring(startIdx + reasonStartTag.length).trimStart();
            response = null;
        }
    } else {
        if (!rawText.includes(reasonEndTag)) {
             reasoning = null;
             response = rawText.trim();
        } else {
             reasoning = null;
             response = rawText.trim();
        }
    }
    if (startIdx !== -1 && rawText.indexOf(reasonEndTag, startIdx) === -1) {
         response = null;
    }
    return { reasoning, response };
}

// --- START: Dedicated Component for Code Blocks ---
// This component CAN use hooks because it starts with an uppercase letter
// and is a valid React functional component.
const CodeBlock = ({ className, children, ...props }) => {
    const [copiedCode, setCopiedCode] = useState(false); // Hook usage is valid here

    const handleCopyCode = useCallback(() => {
        // Use useCallback to prevent recreating function on every render unless children changes
        const codeToCopy = String(children).replace(/\n$/, ''); // Remove trailing newline often added
        navigator.clipboard.writeText(codeToCopy).then(() => {
            setCopiedCode(true);
            setTimeout(() => setCopiedCode(false), 1500); // Reset after 1.5 seconds
        }).catch(err => console.error("Failed to copy code:", err));
    }, [children]); // Dependency array includes children

    const handleKeyDownCopy = useCallback((e) => {
        // Added keyboard accessibility for the button
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCopyCode();
        }
    }, [handleCopyCode]);


    return (
        <div className="relative my-2 group/codeblock"> {/* Use group for hover effect */}
            <pre className={`${CODE_BLOCK_BG} p-3 rounded-md overflow-x-auto text-sm font-mono ${CODE_TEXT_COLOR}`}>
                <code className={className} {...props}>
                    {/* Render children, removing trailing newline */}
                    {String(children).replace(/\n$/, '')}
                </code>
            </pre>
            <button
                onClick={handleCopyCode}
                onKeyDown={handleKeyDownCopy} // Add keyboard handler
                role="button" // Improve accessibility semantics
                tabIndex={0} // Make it focusable
                className={`absolute top-1.5 right-1.5 p-1 text-xs ${copiedCode ? 'bg-emerald-600 text-white' : 'bg-zinc-700 hover:bg-zinc-600 text-zinc-200'} rounded opacity-0 group-hover/codeblock:opacity-100 focus:opacity-100 transition-all duration-200`}
                aria-label={copiedCode ? "Copied!" : "Copy code"}
            >
                {copiedCode ? <Check size={12} /> : <Copy size={12} />}
            </button>
        </div>
    );
};
// --- END: Dedicated Component for Code Blocks ---


// --- Custom Markdown Renderers Object ---
// Now uses the CodeBlock component for block code rendering
const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
        // Use the new CodeBlock component for block code
        return !inline ? (
            <CodeBlock className={className} {...props}>
                {children}
            </CodeBlock>
        ) : (
            // Render inline code as before
            <code className={`${INLINE_CODE_BG} ${INLINE_CODE_TEXT} px-1 py-0.5 rounded text-sm font-mono`} {...props}>
                {children}
            </code>
        );
    },
    // Optional: Style paragraphs for consistent spacing
    p(props) {
       return <p className="mb-2 last:mb-0" {...props} />
    }
    // Add more custom renderers here for lists, headings, etc. if needed
};


// --- Main Component ---
const ThinkingModel = () => {
    // --- State Hooks ---
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState([]); // { sender: 'user'|'ai', rawText: string, reasoning?: string|null, response?: string|null }
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copiedIndex, setCopiedIndex] = useState(null);

    // --- Refs ---
    const chatEndRef = useRef(null);
    const inputRef = useRef(null);

    // --- Agent Details ---
    const agentDetails = { name: 'Echidna v5 Turbo', logoUrl: LOGO_URL };

    // --- Effects ---
    useEffect(() => {
        setTimeout(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
    }, [messages]);

    const adjustTextareaHeight = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            const scrollHeight = inputRef.current.scrollHeight;
            const maxHeight = 160;
            inputRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
            inputRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
        }
    }, []);

    useEffect(() => { adjustTextareaHeight(); }, [inputValue, adjustTextareaHeight]);

    // --- Event Handlers ---
    const handleInputChange = (e) => setInputValue(e.target.value);

    const handleSend = async (e) => {
        // ... (handleSend function remains largely the same) ...
        if (e) e.preventDefault();
        const trimmedInput = inputValue.trim();
        if (trimmedInput === '' || isLoading) return;

        setError(null);
        const newUserMessage = { sender: 'user', rawText: trimmedInput };
        const messageHistory = messages
            .map(msg => ({
                sender: msg.sender,
                text: msg.sender === 'user' ? msg.rawText : (msg.response ?? msg.rawText)
            }))
            .filter(msg => msg.text);

        let currentAIIndex = -1;
        setMessages(prevMessages => {
            const newMessages = [ ...prevMessages, newUserMessage, { sender: 'ai', rawText: '', reasoning: null, response: null }];
            currentAIIndex = newMessages.length - 1;
            return newMessages;
        });

        setInputValue('');
        setIsLoading(true);
        if (inputRef.current) {
             inputRef.current.style.height = 'auto';
             inputRef.current.style.overflowY = 'hidden';
        }

        const payload = { message: trimmedInput, history: messageHistory };

        try {
            const response = await axios.post('https://web-production-5ee8.up.railway.app/echidna/Echidnav5turbo', payload, {
                headers: { 
                    'Content-Type': 'application/json', 
                    'Accept': 'text/event-stream' 
                },
                responseType: 'stream'
            });

            if (!response.data) { throw new Error("Response data is missing."); }

            const reader = response.data.pipeThrough(new TextDecoderStream()).getReader();
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();

                if (done) {
                    setMessages(prev => {
                        const finalMessages = [...prev];
                        if(finalMessages[currentAIIndex]) {
                             const finalRawText = finalMessages[currentAIIndex].rawText;
                             const finalParsed = parseAIResponseStreaming(finalRawText);
                             finalMessages[currentAIIndex] = {
                                 ...finalMessages[currentAIIndex],
                                 reasoning: finalParsed.reasoning,
                                 response: finalParsed.response,
                             }
                        }
                        return finalMessages;
                    })
                    break;
                }

                buffer += value;
                let boundaryIndex;
                while ((boundaryIndex = buffer.indexOf('\n\n')) >= 0) {
                    const messageLine = buffer.slice(0, boundaryIndex);
                    buffer = buffer.slice(boundaryIndex + 2);
                    if (messageLine.startsWith('data: ')) {
                        const dataContent = messageLine.substring(6).trim();
                        if (dataContent) {
                             try {
                                 const jsonData = JSON.parse(dataContent);
                                 if (jsonData.response) {
                                     setMessages(prev => {
                                         const updatedMessages = [...prev];
                                         if (updatedMessages[currentAIIndex]) {
                                             const newRawText = updatedMessages[currentAIIndex].rawText + jsonData.response;
                                             const parsed = parseAIResponseStreaming(newRawText);
                                             updatedMessages[currentAIIndex] = {
                                                 ...updatedMessages[currentAIIndex],
                                                 rawText: newRawText,
                                                 reasoning: parsed.reasoning,
                                                 response: parsed.response
                                             };
                                         }
                                         return updatedMessages;
                                     });
                                 }
                             } catch (parseError) { console.error("SSE Parse Error:", dataContent, parseError); }
                        }
                    }
                }
            }

        } catch (err) {
            console.error("API/Stream Error:", err);
            setError(err.response?.data?.message || err.message || "An unknown error occurred.");
            setMessages(prev => prev.filter((msg, index) => index !== currentAIIndex || msg.rawText !== ''));
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

    const handleCopy = async (msg, index) => {
        const textToCopy = msg.response ?? msg.rawText;
        if (!textToCopy) return;
        try {
            await navigator.clipboard.writeText(textToCopy);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch (err) { console.error("Failed to copy text:", err); }
    };

    const handleCopyKeyDown = (e, callback) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); callback(); } };

    // --- Render Logic ---
    return (
        // ... (JSX rendering remains the same, uses markdownComponents which now uses CodeBlock) ...
        <div className={`flex flex-col h-screen ${BG_COLOR} text-white font-sans`}>
            {/* Header */}
            <div className="p-3 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
                 <div className="flex items-center space-x-3">
                    <div className="h-7 w-7 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0 overflow-hidden">
                         {agentDetails.logoUrl ? (
                            <img src={agentDetails.logoUrl} alt={`${agentDetails.name} logo`} className="h-full w-full object-contain"/>
                         ) : (
                            <Bot size={16} className="text-white" />
                         )}
                    </div>
                    <h1 className="text-md font-medium tracking-tight truncate">{agentDetails.name}</h1>
                 </div>
            </div>

            {/* Content Area: Messages or Empty State */}
            <div className="flex-grow overflow-y-auto py-6 px-4 md:px-0 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-zinc-900">
                {/* Empty State Display */}
                {messages.length === 0 && !isLoading && !error && (
                     <div className="flex flex-col justify-center items-center h-full p-4 space-y-4 text-center">
                        <div className="h-16 w-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4 overflow-hidden">
                             {agentDetails.logoUrl ? (
                                <img src={agentDetails.logoUrl} alt="" className="h-full w-full object-contain"/>
                             ) : (
                                <Bot size={32} className="text-white" />
                             )}
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-white">Chat with {agentDetails.name}</h1>
                        <p className="text-zinc-400 max-w-md text-sm">Start the conversation by typing your message below.</p>
                    </div>
                )}

                {/* Chat Messages Area */}
                {(messages.length > 0 || error) && (
                    <div className="max-w-2xl lg:max-w-3xl mx-auto px-4 space-y-6">
                        {messages.map((msg, index) => (
                            <div key={index} className={`flex items-start space-x-3 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                {/* AI Icon */}
                                {msg.sender === 'ai' && (
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center mt-1 overflow-hidden">
                                         {agentDetails.logoUrl ? (
                                            <img src={agentDetails.logoUrl} alt="Agent" className="w-full h-full object-contain"/>
                                         ) : (
                                            <Bot size={14} className="text-white" />
                                         )}
                                    </div>
                                )}

                                {/* Message Bubble */}
                                <div className={`px-4 py-2.5 rounded-xl ${msg.sender === 'user' ? USER_MSG_BG : AI_MSG_BG} text-white shadow-sm inline-block max-w-[85%] group relative min-h-[38px]`}>
                                    {/* User Message */}
                                    {msg.sender === 'user' && (
                                        <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} className="text-sm leading-relaxed">
                                            {msg.rawText}
                                        </p>
                                    )}

                                    {/* AI Message - Parsed */}
                                    {msg.sender === 'ai' && (msg.reasoning !== null || msg.response !== null) && (
                                        <>
                                            {msg.response ? (
                                                <div className="text-sm leading-relaxed prose prose-sm prose-invert max-w-none">
                                                    {/* Use markdownComponents here */}
                                                    <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>{msg.response}</ReactMarkdown>
                                                </div>
                                            ) : (
                                                isLoading && index === messages.length - 1 && msg.reasoning && !msg.response && (
                                                    <div className="text-xs text-zinc-400 italic py-1">Response loading...</div>
                                                )
                                            )}
                                            {msg.reasoning && (
                                                <details className="mt-3 border-t border-zinc-700 pt-2" open={isLoading && index === messages.length - 1}>
                                                    <summary className="text-xs text-zinc-400 cursor-pointer hover:text-zinc-200 transition-colors outline-none select-none"> Reasoning steps </summary>
                                                    <div className="mt-1.5 text-xs text-zinc-300 bg-zinc-900 p-2 rounded-md border border-zinc-700/50 prose prose-xs prose-invert max-w-none">
                                                        {/* Use markdownComponents here too */}
                                                        <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>{msg.reasoning}</ReactMarkdown>
                                                    </div>
                                                </details>
                                            )}
                                        </>
                                    )}

                                    {/* AI Message - Loading/Fallback */}
                                    {msg.sender === 'ai' && msg.reasoning === null && msg.response === null && (
                                         isLoading && index === messages.length - 1 ? (
                                            <div className="flex items-center justify-center h-[20px]"> <Circle size={14} className="animate-spin text-zinc-400" /> </div>
                                         ) : (
                                            <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} className="text-sm leading-relaxed">
                                                {msg.rawText || '\u00A0'}
                                            </p>
                                         )
                                    )}

                                    {/* Copy Button */}
                                    {msg.sender === 'ai' && (msg.response || msg.rawText) && !isLoading && index !== messages.length - 1 && (
                                         <div className="absolute -bottom-1.5 -right-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                             <div role="button" tabIndex={0} aria-label={copiedIndex === index ? "Copied" : "Copy message"}
                                                 onKeyDown={(e) => handleCopyKeyDown(e, () => handleCopy(msg, index))}
                                                 onClick={() => handleCopy(msg, index)}
                                                 className="p-1 rounded-full bg-zinc-600 hover:bg-zinc-500 cursor-pointer inline-flex items-center">
                                                 {copiedIndex === index ? (<Check size={14} className="text-green-400" />) : (<Copy size={14} className="text-zinc-300" />)}
                                             </div>
                                         </div>
                                     )}
                                </div>

                                 {/* User Icon */}
                                 {msg.sender === 'user' && ( <div className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center"> <User size={14} className="text-white" /></div> )}
                            </div>
                        ))}

                        {/* Error Display */}
                        {error && !isLoading && ( <div className="text-red-400 text-sm text-center mt-2 bg-red-900/20 p-3 rounded-lg border border-red-800/50">Error: {error}</div>)}
                        {/* Scroll Anchor */}
                        <div ref={chatEndRef} />
                    </div>
                )}
            </div>

            {/* Input Area */}
            <div className={`w-full py-3 px-4 ${BG_COLOR} border-t border-zinc-800 flex-shrink-0`}>
                <form onSubmit={handleSend} className="flex items-end w-full max-w-2xl lg:max-w-3xl mx-auto rounded-xl overflow-hidden shadow-sm border border-zinc-700 focus-within:border-zinc-600 transition-colors duration-200 bg-zinc-900">
                    <textarea
                        ref={inputRef} rows="1" value={inputValue} onChange={handleInputChange} onKeyDown={handleKeyDown}
                        placeholder={`Message ${agentDetails.name}...`} disabled={isLoading}
                        className="flex-grow bg-transparent border-none focus:ring-0 focus:outline-none text-white placeholder-zinc-500 resize-none py-2.5 px-4 text-sm max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-600 scrollbar-track-zinc-800"
                        aria-label="Chat input"
                    />
                    <div className="p-1.5 self-end">
                        <button type="submit" aria-label="Send message" disabled={isLoading || !inputValue.trim()}
                            className={`p-2 flex items-center justify-center rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900 focus:ring-white ${ (inputValue.trim() && !isLoading) ? `${BUTTON_COLOR}` : `${DISABLED_BUTTON}`}`}>
                            {isLoading ? ( <Circle size={18} className="animate-spin text-white" /> ) : ( <Send size={18} strokeWidth={2.5} /> )}
                        </button>
                    </div>
                </form>
                 <p className="text-xs text-zinc-500 text-center mt-2 px-4">{DISCLAIMER_TEXT}</p>
            </div>
        </div>

    );
};

export default ThinkingModel;